// Presentational cards for files Phoebe delivers into the chat. Data
// fetching and download orchestration live in ui/chat_attachment_data.ts;
// these stay pure so the component gallery can render every state.
import {
    Archive,
    Download,
    File as FileIcon,
    FileText,
    Image as ImageIcon,
    Table,
} from 'lucide-react';
import {
    type ComponentType,
    createContext,
    type ImgHTMLAttributes,
    type ReactNode,
    useContext,
} from 'react';
import { type Components } from 'react-markdown';

import { Markdown } from '@apps/web/components/ui/markdown';
import { Spinner } from '@apps/web/components/ui/spinner';
import { cn } from '@apps/web/lib/utils';
import { type AttachmentPreviewRead } from '@libraries/typescript/api_client';

const AttachmentGroupContext = createContext(false);

export function AttachmentGroup({
    children,
}: {
    readonly children: ReactNode;
}) {
    return (
        <AttachmentGroupContext.Provider value={true}>
            <div
                role="group"
                aria-label="Attachments"
                className="bg-card w-full max-w-[560px] divide-y overflow-hidden rounded-lg border"
            >
                {children}
            </div>
        </AttachmentGroupContext.Provider>
    );
}

export function formatAttachmentSize(sizeBytes: number): string {
    if (sizeBytes < 1024) {
        return `${sizeBytes} B`;
    }
    if (sizeBytes < 1024 * 1024) {
        const kilobytes = sizeBytes / 1024;
        return `${kilobytes.toFixed(kilobytes >= 10 ? 0 : 1)} KB`;
    }
    const megabytes = sizeBytes / (1024 * 1024);
    return `${megabytes.toFixed(megabytes >= 10 ? 0 : 1)} MB`;
}

function countLabel(count: number, truncated: boolean, noun: string): string {
    const plural = count === 1 && !truncated ? noun : `${noun}s`;
    return `${count.toLocaleString()}${truncated ? '+' : ''} ${plural}`;
}

/**
 * The count a coordinator wants after kind and size, or undefined when the
 * kind has none. Counts read from a bounded prefix carry a `+`, because they
 * are a floor rather than the file's total.
 */
export function attachmentShapeLine(
    preview: AttachmentPreviewRead,
): string | undefined {
    const body = preview.preview;
    switch (body.kind) {
        case 'table':
            return `${countLabel(body.previewed_row_count, preview.truncated, 'row')} × ${countLabel(body.previewed_column_count, preview.truncated, 'column')}`;
        case 'text':
            return body.text_format === 'markdown'
                ? undefined
                : countLabel(
                      body.previewed_line_count,
                      preview.truncated,
                      'line',
                  );
        case 'archive':
            return countLabel(body.entry_count, false, body.entry_kind);
        default:
            return undefined;
    }
}

// Every non-image attachment is served as application/octet-stream, so the
// filename extension — validated server-side against a closed allowlist —
// is the only signal for the type tile and label.
type FileKind = 'sheet' | 'pdf' | 'md' | 'text' | 'zip' | 'image' | 'file';

type AttachmentFileType = {
    readonly kind: FileKind;
    readonly label: string;
};

const EXTENSION_FILE_TYPES: Readonly<Record<string, AttachmentFileType>> = {
    // keep-sorted start
    csv: { kind: 'sheet', label: 'CSV' },
    gif: { kind: 'image', label: 'GIF' },
    jpeg: { kind: 'image', label: 'JPEG' },
    jpg: { kind: 'image', label: 'JPEG' },
    json: { kind: 'text', label: 'JSON' },
    log: { kind: 'text', label: 'Log' },
    markdown: { kind: 'md', label: 'Markdown' },
    md: { kind: 'md', label: 'Markdown' },
    ods: { kind: 'sheet', label: 'Spreadsheet' },
    pdf: { kind: 'pdf', label: 'PDF' },
    png: { kind: 'image', label: 'PNG' },
    tsv: { kind: 'sheet', label: 'TSV' },
    txt: { kind: 'text', label: 'Text' },
    webp: { kind: 'image', label: 'WebP' },
    xlsx: { kind: 'sheet', label: 'Spreadsheet' },
    zip: { kind: 'zip', label: 'ZIP' },
    // keep-sorted end
};

const GENERIC_FILE_TYPE: AttachmentFileType = { kind: 'file', label: 'File' };

export function fileTypeForFilename(filename: string): AttachmentFileType {
    // The backend lowercases extensions when validating, so REPORT.CSV is
    // legal input; unknown extensions (user uploads, future allowlist
    // growth) get the generic tile instead of breaking.
    const dotIndex = filename.lastIndexOf('.');
    const extension =
        dotIndex > 0 ? filename.slice(dotIndex + 1).toLowerCase() : '';
    return EXTENSION_FILE_TYPES[extension] ?? GENERIC_FILE_TYPE;
}

const TILE_KIND_CLASSES: Readonly<Record<FileKind, string>> = {
    file: 'bg-[#96A7AA]',
    image: 'bg-[#0EA5E9]',
    md: 'bg-[#5533EE]',
    pdf: 'bg-[#E5484D]',
    sheet: 'bg-[#1F9D6B]',
    text: 'bg-[#64748B]',
    zip: 'bg-[#D97706]',
};

const TILE_KIND_ICONS: Readonly<
    Record<FileKind, ComponentType<{ readonly className?: string }>>
> = {
    file: FileIcon,
    image: ImageIcon,
    md: FileText,
    pdf: FileIcon,
    sheet: Table,
    text: FileText,
    zip: Archive,
};

export function FileTypeTile({
    fileType,
    className,
}: {
    readonly fileType: AttachmentFileType;
    readonly className?: string;
}) {
    const Icon = TILE_KIND_ICONS[fileType.kind];
    return (
        <span
            aria-hidden="true"
            data-testid="file-type-tile"
            data-file-kind={fileType.kind}
            className={cn(
                'flex size-9 shrink-0 items-center justify-center rounded-md text-white [&>svg]:size-[18px]',
                TILE_KIND_CLASSES[fileType.kind],
                className,
            )}
        >
            <Icon />
        </span>
    );
}

function attachmentDownloadLabel(
    filename: string,
    downloadError: string | null,
): string {
    return downloadError
        ? `Download ${filename} (previous attempt failed: ${downloadError})`
        : `Download ${filename}`;
}

type AttachmentDownloadProps = {
    readonly filename: string;
    readonly sizeBytes: number;
    readonly isDownloading: boolean;
    readonly downloadError: string | null;
    readonly onDownload: () => void;
};

function AttachmentDownloadButton({
    filename,
    isDownloading,
    downloadError,
    onDownload,
}: Omit<AttachmentDownloadProps, 'sizeBytes'>) {
    return (
        <button
            type="button"
            disabled={isDownloading}
            onClick={onDownload}
            aria-label={attachmentDownloadLabel(filename, downloadError)}
            className="text-muted-foreground/60 group-hover/card:text-agent-action relative z-20 ml-1 shrink-0 rounded-md p-1 disabled:cursor-progress disabled:opacity-70 dark:group-hover/card:text-violet-300 [&>svg]:size-[15px]"
        >
            {isDownloading ? (
                <Spinner size="sm" />
            ) : (
                <Download aria-hidden="true" />
            )}
        </button>
    );
}

function AttachmentMetaBar({
    filename,
    sizeBytes,
    shapeLine,
    hasPreviewBody,
    isDownloading,
    downloadError,
    onDownload,
}: AttachmentDownloadProps & {
    readonly shapeLine: string | undefined;
    readonly hasPreviewBody: boolean;
}) {
    const fileType = fileTypeForFilename(filename);
    const metaParts = [fileType.label, formatAttachmentSize(sizeBytes)];
    if (shapeLine !== undefined) {
        metaParts.push(shapeLine);
    }
    return (
        <div
            data-testid="chat-attachment-meta-bar"
            className={cn(
                'flex items-center gap-2.5 py-2 pr-3.5 pl-3',
                hasPreviewBody && 'border-t',
            )}
        >
            <FileTypeTile
                fileType={fileType}
                className="size-[30px] [&>svg]:size-[15px]"
            />
            <span className="min-w-0 flex-1">
                <span className="text-foreground text-v3-chat block truncate font-medium">
                    {filename}
                </span>
                <span className="text-muted-foreground text-v3-chat-meta block truncate">
                    {metaParts.join(' · ')}
                </span>
            </span>
            <AttachmentDownloadButton
                filename={filename}
                isDownloading={isDownloading}
                downloadError={downloadError}
                onDownload={onDownload}
            />
        </div>
    );
}

export type AttachmentCardProps = AttachmentDownloadProps & {
    /** Kind, size and count suffix for the meta bar's second line. */
    readonly shapeLine?: string;
    readonly onOpen: () => void;
    /** Preview body; omitted when the file has no preview. */
    readonly children?: ReactNode;
};

export function AttachmentCard({
    filename,
    sizeBytes,
    shapeLine,
    isDownloading,
    downloadError,
    onOpen,
    onDownload,
    children,
}: AttachmentCardProps) {
    const grouped = useContext(AttachmentGroupContext);
    return (
        <div
            data-testid="chat-attachment-file-card"
            className={cn(
                'group/card relative overflow-hidden transition-colors hover:bg-[#F7FAFA] dark:hover:bg-muted has-[:focus-visible]:outline-agent-action has-[:focus-visible]:outline-2 has-[:focus-visible]:-outline-offset-2',
                grouped ? 'w-full' : 'bg-card max-w-[560px] rounded-lg border',
            )}
        >
            {/* Stretched link: a download button nested inside the card's
                own button would be invalid HTML. */}
            <button
                type="button"
                onClick={onOpen}
                aria-label={`Open ${filename}`}
                className="absolute inset-0 z-10 outline-none"
            />
            {children}
            <AttachmentMetaBar
                filename={filename}
                sizeBytes={sizeBytes}
                shapeLine={shapeLine}
                hasPreviewBody={children !== undefined}
                isDownloading={isDownloading}
                downloadError={downloadError}
                onDownload={onDownload}
            />
        </div>
    );
}

// One height for every preview body so a stack of mixed files keeps an even
// rhythm while scrolling.
const PREVIEW_BODY_CLASSES = 'relative h-[132px] overflow-hidden';

// Prose fades because a half-cut line reads as "continues"; a table keeps a
// clean row edge, because a half-cut row reads as broken.
function PreviewBottomFade() {
    return (
        <div
            aria-hidden="true"
            className="to-card pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-b from-transparent"
        />
    );
}

function PreviewRightFade() {
    return (
        <div
            aria-hidden="true"
            className="to-card pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-r from-transparent"
        />
    );
}

export function AttachmentPreviewLoadingBody({
    filename,
}: {
    readonly filename: string;
}) {
    return (
        <div
            className={cn(
                PREVIEW_BODY_CLASSES,
                'text-muted-foreground text-v3-chat flex items-center gap-2 px-4',
            )}
        >
            <Spinner size="sm" />
            <span>Loading {filename}…</span>
        </div>
    );
}

// Hardened <img> renderer for agent-attached markdown: only inline data URIs
// (embedded PNG/JPEG etc.) render. Every remote URL turns into a plain-text
// placeholder so opening the file cannot leak the viewer's IP to an arbitrary
// origin (react-markdown does not sanitize the img src attribute on its own).
export const ATTACHMENT_MARKDOWN_COMPONENTS: Components = {
    img: ({ src, alt }: ImgHTMLAttributes<HTMLImageElement>) => {
        if (typeof src === 'string' && src.startsWith('data:')) {
            return (
                <img
                    src={src}
                    alt={alt}
                    referrerPolicy="no-referrer"
                    loading="lazy"
                    className="my-2 h-auto max-h-96 max-w-[80%] rounded-md border"
                />
            );
        }
        return (
            <span className="text-muted-foreground italic">
                [image omitted: {alt || 'remote image blocked'}]
            </span>
        );
    },
};

export function MarkdownPreviewBody({
    markdown,
}: {
    readonly markdown: string;
}) {
    return (
        <div className={cn(PREVIEW_BODY_CLASSES, 'px-4 pt-3.5')}>
            <Markdown
                components={ATTACHMENT_MARKDOWN_COMPONENTS}
                skipHtml={true}
                className="[&_code]:text-v3-chat-meta [&_h1]:text-v3-chat [&_h2]:text-v3-chat [&_h3]:text-v3-chat [&_li]:text-v3-chat [&_p]:text-v3-chat [&_td]:text-v3-chat [&_th]:text-v3-chat-meta [&_li]:leading-(--text-v3-chat--line-height) [&_p]:leading-(--text-v3-chat--line-height)"
            >
                {markdown}
            </Markdown>
            <PreviewBottomFade />
        </div>
    );
}

const TEXT_PREVIEW_LINES = 8;

export function TextPreviewBody({ text }: { readonly text: string }) {
    return (
        <div className={cn(PREVIEW_BODY_CLASSES, 'px-4 pt-3')}>
            <pre className="text-foreground text-v3-chat-meta font-mono whitespace-pre">
                {text.split('\n').slice(0, TEXT_PREVIEW_LINES).join('\n')}
            </pre>
            <PreviewRightFade />
            <PreviewBottomFade />
        </div>
    );
}

const TABLE_PREVIEW_ROWS = 3;

export function TablePreviewBody({
    columns,
    rows,
}: {
    readonly columns: ReadonlyArray<string>;
    readonly rows: ReadonlyArray<ReadonlyArray<string>>;
}) {
    return (
        <div className={cn(PREVIEW_BODY_CLASSES, 'px-4 pt-3')}>
            <table className="text-v3-chat-meta w-max border-collapse whitespace-nowrap">
                <thead>
                    <tr>
                        {columns.map((column, index) => (
                            <th
                                key={`${index}-${column}`}
                                className="text-muted-foreground px-2 py-1 text-left font-medium first:pl-0"
                            >
                                {column}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.slice(0, TABLE_PREVIEW_ROWS).map((row, rowIndex) => (
                        <tr key={rowIndex}>
                            {row.map((cell, cellIndex) => (
                                <td
                                    key={`${cellIndex}-${cell}`}
                                    className="text-foreground px-2 py-1 first:pl-0"
                                >
                                    {cell}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            <PreviewRightFade />
        </div>
    );
}

const ENTRY_PREVIEW_COUNT = 4;

export function EntryListPreviewBody({
    entries,
    entryCount,
}: {
    readonly entries: ReadonlyArray<string>;
    readonly entryCount: number;
}) {
    const visible = entries.slice(0, ENTRY_PREVIEW_COUNT);
    const remaining = entryCount - visible.length;
    return (
        <div className={cn(PREVIEW_BODY_CLASSES, 'space-y-0.5 px-4 pt-3')}>
            {visible.map((entry, index) => (
                <div
                    key={`${index}-${entry}`}
                    className="text-foreground text-v3-chat-meta truncate font-mono"
                >
                    {entry}
                </div>
            ))}
            {remaining > 0 && (
                <div className="text-muted-foreground text-v3-chat-meta">
                    +{remaining} more
                </div>
            )}
        </div>
    );
}

export function PdfPreviewBody({
    filename,
    isRendering,
    children,
}: {
    readonly filename: string;
    readonly isRendering: boolean;
    readonly children: ReactNode;
}) {
    return (
        <div
            className={cn(PREVIEW_BODY_CLASSES, 'bg-muted pointer-events-none')}
        >
            <div aria-hidden="true" className={cn(isRendering && 'invisible')}>
                {children}
            </div>
            {isRendering && (
                <div className="absolute inset-0">
                    <AttachmentPreviewLoadingBody filename={filename} />
                </div>
            )}
        </div>
    );
}

export type AttachmentImageCardProps = AttachmentDownloadProps & {
    /** Object or data URL for the loaded image; null while loading. */
    readonly imageUrl: string | null;
    readonly onOpen: () => void;
};

export function AttachmentImageCard({
    filename,
    sizeBytes,
    imageUrl,
    isDownloading,
    downloadError,
    onOpen,
    onDownload,
}: AttachmentImageCardProps) {
    const grouped = useContext(AttachmentGroupContext);
    return (
        <div
            data-testid="chat-attachment-image-card"
            className={cn(
                'group/image relative rounded-lg has-[:focus-visible]:outline-agent-action has-[:focus-visible]:outline-2 has-[:focus-visible]:-outline-offset-2',
                grouped ? 'w-full p-2' : 'max-w-[440px]',
            )}
        >
            <button
                type="button"
                onClick={onOpen}
                aria-label={`Open ${filename}`}
                className="absolute inset-0 z-10 outline-none"
            />
            <div
                className={cn(
                    'relative overflow-hidden',
                    grouped ? 'rounded-md' : 'rounded-lg border',
                )}
            >
                {imageUrl ? (
                    <img
                        src={imageUrl}
                        alt={filename}
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        className="max-h-[420px] w-auto max-w-full object-contain"
                    />
                ) : (
                    <div className="bg-muted text-muted-foreground text-v3-chat-meta flex h-32 items-center justify-center">
                        Loading {filename}…
                    </div>
                )}
                <button
                    type="button"
                    disabled={isDownloading}
                    onClick={onDownload}
                    aria-label={attachmentDownloadLabel(
                        filename,
                        downloadError,
                    )}
                    className="absolute top-2.5 right-2.5 z-20 grid size-[30px] place-items-center rounded-md bg-[#001418]/55 text-white opacity-0 backdrop-blur-[4px] transition-opacity group-hover/image:opacity-100 focus-visible:opacity-100 disabled:cursor-progress disabled:opacity-100 [&>svg]:size-[15px]"
                >
                    {isDownloading ? (
                        <Spinner size="sm" />
                    ) : (
                        <Download aria-hidden="true" />
                    )}
                </button>
            </div>
            <div className="text-muted-foreground text-v3-chat-meta mt-1.5 truncate">
                {filename} &middot; {formatAttachmentSize(sizeBytes)}
            </div>
        </div>
    );
}
